import type { WidgetEmbedParseResult } from '@/components/widget/widget-config';
import { parseLauncherPosition } from '@/lib/engage/launcher-position';

/** Public payload from `GET /api/engage/widget/theme`. */
export type WidgetThemeApiPayload = {
  readonly accent_color?: string;
  readonly launcher_fill_color?: string;
  readonly launcher_text_color?: string;
  readonly user_text_color?: string;
  readonly reply_color?: string;
  readonly reply_text_color?: string;
  readonly surface_color?: string;
  readonly bubble_shape?: string;
  readonly chat_window_title?: string;
  readonly launcher_text?: string;
  readonly corner_radius?: number;
  readonly header_gradient?: boolean;
  readonly show_avatars?: boolean;
  readonly launcher_radius?: number;
  readonly launcher_position_desktop?: string;
  readonly launcher_position_mobile?: string;
};

/**
 * When `data-theme-lock="embed"`, the pasted snippet colors win (advanced override).
 * Otherwise the portal-saved theme from the API is canonical on every page load.
 */
export function isEmbedThemeLocked(script: Pick<HTMLScriptElement, 'getAttribute'>): boolean {
  const lock = script.getAttribute('data-theme-lock')?.trim().toLowerCase();
  return lock === 'embed' || lock === 'true' || lock === '1';
}

/**
 * Merges operator-saved theme from `/api/engage/widget/theme` over embed defaults.
 * Portal **Save Theme** is the source of truth unless the embed opts out via
 * `data-theme-lock="embed"`.
 */
export function mergeEmbedWithRemoteTheme(
  script: Pick<HTMLScriptElement, 'getAttribute'>,
  embed: WidgetEmbedParseResult,
  remote: WidgetThemeApiPayload | null,
): WidgetEmbedParseResult {
  if (!remote || isEmbedThemeLocked(script)) {
    return embed;
  }

  const userBubble =
    remote.accent_color?.trim() || remote.launcher_fill_color?.trim() || undefined;
  const launcherFill =
    remote.launcher_fill_color?.trim() || remote.accent_color?.trim() || undefined;
  const surface = remote.surface_color?.trim() || undefined;
  const replyBubble = remote.reply_color?.trim() || undefined;
  const chatTitle = remote.chat_window_title?.trim() || undefined;
  const launcherText = remote.launcher_text?.trim() || undefined;
  const positionDesktop = parseLauncherPosition(remote.launcher_position_desktop);
  const positionMobile = parseLauncherPosition(remote.launcher_position_mobile);

  return {
    ...embed,
    ...(userBubble ? { userBubbleColor: userBubble } : {}),
    ...(launcherFill ? { launcherFillColor: launcherFill } : {}),
    ...(surface ? { surfaceColor: surface } : {}),
    ...(replyBubble ? { replyBubbleColor: replyBubble } : {}),
    ...(chatTitle ? { chatWindowTitle: chatTitle } : {}),
    ...(launcherText ? { launcherButtonText: launcherText } : {}),
    ...(remote.user_text_color?.trim()
      ? { userTextColor: remote.user_text_color.trim() }
      : {}),
    ...(remote.reply_text_color?.trim()
      ? { replyTextColor: remote.reply_text_color.trim() }
      : {}),
    ...(remote.launcher_text_color?.trim()
      ? { launcherTextColor: remote.launcher_text_color.trim() }
      : {}),
    ...(typeof remote.header_gradient === 'boolean'
      ? { headerGradient: remote.header_gradient }
      : {}),
    ...(typeof remote.show_avatars === 'boolean' ? { showAvatars: remote.show_avatars } : {}),
    ...(typeof remote.corner_radius === 'number' && !Number.isNaN(remote.corner_radius)
      ? { cornerRadius: remote.corner_radius }
      : {}),
    ...(typeof remote.launcher_radius === 'number' && !Number.isNaN(remote.launcher_radius)
      ? { launcherRadius: remote.launcher_radius }
      : {}),
    ...(remote.bubble_shape === 'pill' || remote.bubble_shape === 'rounded'
      ? { bubbleShape: remote.bubble_shape }
      : {}),
    ...(positionDesktop
      ? { position: positionDesktop, positionDesktop }
      : {}),
    ...(positionMobile ? { positionMobile } : {}),
  };
}
